import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons/faCircleExclamation";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Tooltip } from "../../../../utils/local-antd";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { SpinnerFullScreen } from "../../../../components/SpinnerFullScreen";
import { Text } from "../../../../components/Text";
import { Modal } from "../../../../components/modal/Modal";
import {
  DEFAULT_INVITE_TEAM_ROLE,
  TEAM_ROLES,
} from "../../../../model/permissions/team-role.config";
import { PermissionInviteStatus } from "../../../../model/permissions/types/PermissionInviteStatus";
import { permissionSliceSelectors } from "../../../../redux/permission/permission.selector";
import { permissionActions } from "../../../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { userActions } from "../../../../redux/user/user.slice";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { Info } from "../../../../components/Info";

const LoadingOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.white};
  opacity: 0.8;
  z-index: 1;
  display: flex;
`;

const NeutralButton = styled(Button)`
  margin: 0 auto;
  width: 50%;
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.white};
`;

const AcceptButton = styled(Button)`
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.white};
`;

const RejectButton = styled(Button)`
  background-color: ${({ theme }) => theme.danger};
  color: ${({ theme }) => theme.white};
`;

export const InviteNeedActionModal: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const currentInvite = useAppSelector(
    permissionSliceSelectors.selectCurrentInvite,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const canShowModal =
    currentInvite &&
    currentInvite.inviteeEmail === user?.email &&
    [PermissionInviteStatus.CREATED, PermissionInviteStatus.PENDING].includes(
      currentInvite.status,
    );
  const canShowWrongEmailModal =
    currentInvite &&
    currentInvite.inviteeEmail !== user?.email &&
    [PermissionInviteStatus.CREATED, PermissionInviteStatus.PENDING].includes(
      currentInvite.status,
    );

  const onNeutralClick = () => {
    setOpen(false);
    dispatch(permissionActions.resetCurrentInvite({}));
  };

  const onRespondToInvite = async (accept: boolean) => {
    try {
      setLoading(true);
      await dispatch(
        permissionActions.respondToInvite({
          accept,
          userId: user?._id!,
          inviteCode: currentInvite?.inviteCode!,
        }),
      ).unwrap();
      dispatch(permissionActions.resetCurrentInvite({}));
      await dispatch(
        userActions.getLoggedInUser({ userId: user?._id! }),
      ).unwrap();
      setOpen(false);

      if (accept) {
        AtiraToast.success(
          t("permissions.invite.pending-action.modal.accept", {
            name: currentInvite?.inviter?.name || "Atira Space user",
          }),
        );
        setTimeout(() => {
          window.location.reload();
        }, 6000);
      } else {
        AtiraToast.info(t("permissions.invite.pending-action.modal.reject"));
        window.history.replaceState({}, "", pathname.split("?")[0]);
        searchParams.delete("inviteCode");
        searchParams.delete("inviteMode");
      }
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  if (canShowWrongEmailModal) {
    return (
      <Modal
        maskClosable={false}
        open={open}
        title={t("permissions.invite.title")}
      >
        <Flex flexDirection="column" gap="m" position="relative">
          <Text fontSize="l" align="center">
            {t("permissions.invite.different-email.modal.title")}
          </Text>
          <Text color="darkerSub" align="center">
            {t("permissions.invite.different-email.modal.description")}
          </Text>

          <NeutralButton
            onClick={onNeutralClick}
            title={t("permissions.invite.different-email.modal.button")}
          />
        </Flex>
      </Modal>
    );
  }

  if (!canShowModal) {
    return null;
  }

  const inviteRole = currentInvite.orgRole || DEFAULT_INVITE_TEAM_ROLE;

  return (
    <Modal
      maskClosable={false}
      open={open}
      title={t("permissions.invite.title")}
    >
      <Flex flexDirection="column" gap="m" position="relative">
        <Text fontSize="l" align="center">
          {t("permissions.invite.pending-action.modal.title", {
            name: currentInvite.inviter?.name || "Atira Space user",
          })}
        </Text>
        <Text color="darkerSub">
          {t("permissions.invite.pending-action.modal.title-required")}
        </Text>
        <Text color="darkerSub">
          {t("permissions.invite.pending-action.modal.description")}
        </Text>
        <Info
          info={t("permissions.invite.pending-action.modal.warning", {
            role: t(`permissions.team-role.${inviteRole}`),
            actions: TEAM_ROLES.find(({ id }) => id === inviteRole)
              ?.defaultActions.map((action) =>
                t(`common.${action.toLowerCase()}`),
              )
              .join(", "),
          })}
        />
        {loading ? (
          <LoadingOverlay>
            <SpinnerFullScreen />
          </LoadingOverlay>
        ) : null}

        <Flex gap="m" justifyContent="center" alignItems="center">
          <Flex>
            <Tooltip
              title={t(
                "permissions.invite.pending-action.modal.button.accept-description",
              )}
            >
              <Flex cursor="pointer">
                <AtiraIcon icon={faCircleExclamation} color="gray" />
              </Flex>
            </Tooltip>
            <AcceptButton
              onClick={() => onRespondToInvite(true)}
              title={t(
                "permissions.invite.pending-action.modal.button.accept-title",
              )}
              disabled={loading}
            />
          </Flex>

          <Flex>
            <Tooltip
              title={t(
                "permissions.invite.pending-action.modal.button.cancel-description",
              )}
            >
              <Flex cursor="pointer">
                <AtiraIcon icon={faCircleExclamation} color="gray" />
              </Flex>
            </Tooltip>
            <RejectButton
              onClick={() => onRespondToInvite(false)}
              title={t(
                "permissions.invite.pending-action.modal.button.cancel-title",
              )}
              disabled={loading}
            />
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
